"use client";
import React, { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useGetReviews, useGetTestimonials } from '@/actions/testimonial';
import BlobImage from '@/components/Home/BlobImage';
import AvisCard from '../Avis/AvisCard';
import Button from '../ui/Button';
import SendAvisModal from '../Fiche/SendAvisModal';
import type { IAvis } from '@/types/testimonial';

interface TestimonialsProps {
    /** Show the grid of latest avis below the featured block (used on the fiche page). */
    testimonialCards?: boolean;
    /** Show the "Nous contacter" button under the grid. */
    showButton?: boolean;
    /**
     * Reviews to show in the grid. When provided (e.g. the current formation's
     * own reviews on the fiche page), these are used instead of the global
     * latest approved avis — and the /reviews request is skipped entirely.
     */
    avis?: IAvis[];
    /** Formation name shown in the "Donnez votre avis" modal subtitle. */
    programName?: string;
    /** Slug of the formation an avis is attached to — required to submit from the modal. */
    formationSlug?: string;
}

/**
 * "Témoignages" block — features the first published testimonial, and
 * optionally a grid of the 4 latest approved avis (reviews) below it.
 * Testimonials come from GET /api/v1/testimonials. The avis grid shows the
 * formation-specific `avis` prop when given (already included in the formation
 * detail payload), otherwise the global latest approved avis from
 * GET /api/v1/reviews. The section hides itself while loading or when there
 * is no featured testimonial to show.
 */
const Testimonials: React.FC<TestimonialsProps> = ({ testimonialCards = false, showButton = true, avis, programName, formationSlug }) => {
    const { testimonials, testimonialsLoading } = useGetTestimonials();
    // Only hit /reviews when no formation-specific avis were supplied.
    const { reviews } = useGetReviews(avis ? null : 1);
    const testimonial = testimonials[0];
    const [isAvisOpen, setIsAvisOpen] = useState(false);

    // Nothing to feature yet (loading, empty, or error) — omit the section.
    if (testimonialsLoading || !testimonial) return null;

    // The grid shows the 4 most recent avis — the formation's own when provided.
    const latestAvis = (avis ?? reviews).slice(0, 4);
    const showGrid = testimonialCards && latestAvis.length > 0;

    // Up to 3 admin-defined CTA buttons (may be empty — then no button shows).
    const ctaButtons = testimonial.buttons ?? [];

    return (
        <section className="">
            <div className={`w-full bg-[#1B365E] text-white py-10 md:pt-13 ${showGrid ? "md:pb-36.5" : "md:pb-15.5"} px-4 relative overflow-x-hidden z-2`}>
                <div className="max-w-260 mx-auto relative z-10">
                    <h2 className="font-red-hat text-[30px] md:text-[40px] leading-10 md:leading-13.25 font-light tracking-[0.2rem] text-center">
                        Témoignages
                    </h2>

                    <div className="mt-4 md:mt-10 flex md:flex-row lg:justify-between justify-center flex-col items-start gap-10">
                        {/* Left - Portrait, masked into an organic "blob" with a gradient border */}
                        {testimonial.image && (
                            <BlobImage src={testimonial.image} alt="Profile" />
                        )}

                        {/* Right - Content */}
                        <div className="flex-1 text-center md:text-left max-w-171.5">
                            {/* Header */}
                            <div className="w-full flex flex-col-reverse md:flex-row md:items-start justify-center md:justify-between gap-4 mb-4 md:mb-10">
                                <div className="max-w-107.5 mx-auto md:mx-0">
                                    <h3 className="text-[16px] leading-6.5 font-bold tracking-[0.32px]">
                                        {testimonial.name}
                                        {testimonial.role && (
                                            <>, <span className="font-normal">{testimonial.role}</span></>
                                        )}
                                    </h3>
                                </div>
                                <div className="flex items-center gap-2 justify-center md:py-0 py-1">
                                    <span className="text-white text-[15px] leading-5 tracking-[0.2px] font-bold mr-0.5">{testimonial.rating}</span>
                                    <svg width="19" height="18" viewBox="0 0 19 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M8.38586 0.691212C8.68521 -0.230099 9.98862 -0.2301 10.288 0.691211L11.8066 5.36497C11.9404 5.777 12.3244 6.05596 12.7576 6.05596H17.6719C18.6406 6.05596 19.0434 7.29557 18.2597 7.86497L14.284 10.7535C13.9335 11.0082 13.7868 11.4595 13.9207 11.8716L15.4393 16.5453C15.7386 17.4666 14.6842 18.2327 13.9004 17.6633L9.9247 14.7748C9.57421 14.5202 9.09962 14.5202 8.74913 14.7748L4.77339 17.6633C3.98968 18.2327 2.9352 17.4666 3.23455 16.5453L4.75314 11.8716C4.88702 11.4595 4.74036 11.0082 4.38987 10.7535L0.414135 7.86497C-0.369579 7.29557 0.0331967 6.05596 1.00192 6.05596H5.9162C6.34943 6.05596 6.73339 5.777 6.86726 5.36497L8.38586 0.691212Z" fill="#FFBC42" />
                                    </svg>
                                    <span className="text-[#40BFDE] text-[12px] leading-5 font-normal tracking-[0.2px]">{testimonial.date}</span>
                                </div>
                            </div>

                            {/* Content */}
                            <div className="space-y-4 sm:space-y-5">
                                <p className="text-white text-[12px] sm:text-[14px] leading-5 font-normal tracking-[0.3px]">
                                    {testimonial.review}
                                </p>
                            </div>

                            {/* CTA Buttons — up to 3 admin-defined links */}
                            {ctaButtons.length > 0 && (
                                <div className="mt-7 sm:mt-8 flex flex-col md:flex-row md:flex-wrap items-center md:items-start justify-center md:justify-start gap-4 md:gap-x-8 md:gap-y-3">
                                    {ctaButtons.map((button, index) => {
                                        const isExternal = /^https?:\/\//i.test(button.link);
                                        return (
                                            <Link
                                                key={`${button.link}-${index}`}
                                                href={button.link}
                                                {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                                                className="flex items-center justify-center w-full md:w-auto cursor-pointer gap-2 text-white hover:text-[#40BFDE] transition-colors duration-300 group"
                                            >
                                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path fillRule="evenodd" clipRule="evenodd" d="M15 8C15 4.13401 11.866 1 8 1C4.13401 1 1 4.13401 1 8C1 11.866 4.13401 15 8 15C11.866 15 15 11.866 15 8ZM4.75 7.25C4.33579 7.25 4 7.58579 4 8C4 8.41421 4.33579 8.75 4.75 8.75L9.43934 8.75L8.21967 9.96967C7.92678 10.2626 7.92678 10.7374 8.21967 11.0303C8.51256 11.3232 8.98744 11.3232 9.28033 11.0303L11.7803 8.53033C12.0732 8.23744 12.0732 7.76256 11.7803 7.46967L9.28033 4.96967C8.98744 4.67678 8.51256 4.67678 8.21967 4.96967C7.92678 5.26256 7.92678 5.73744 8.21967 6.03033L9.43934 7.25L4.75 7.25Z" fill="currentColor" />
                                                </svg>
                                                <span className="text-[15px] leading-4 font-bold tracking-[0.2px]">{button.label}</span>
                                            </Link>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>

                </div>
                {/* Background */}
                <div className="absolute left-32 sm:left-0 inset-0 top-0 w-full h-full">
                    <Image
                        src="/assets/testimonials.svg"
                        alt="Background decoration"
                        title='Background decoration'
                        fill
                        className='object-cover xl:object-contain'
                    />
                </div>
            </div>

            {/* Latest avis grid */}
            <div className="px-4">
                {showGrid && (
                    <div className="max-w-7xl mx-auto flex flex-col items-center justify-center">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 xl:gap-11.5 md:mb-14.25 mb-6 md:-mt-20 mt-[29.66px] relative z-3">
                        {latestAvis.map((avis) => (
                        <AvisCard key={avis.id} avis={avis} />
                        ))}
                    </div>
                    </div>
                )}

                {showButton && (
                    <div className="max-w-7xl mx-auto mb-4 flex justify-center">
                    <Button
                        variant="outline-gradient"
                        onClick={() => setIsAvisOpen(true)}
                        className="sm:w-53.25 w-full py-2.25! h-9! text-[15px] font-bold md:mb-15 m-4"
                    >
                        Ajouter un avis
                    </Button>
                    </div>
                )}
            </div>

            <SendAvisModal
                isOpen={isAvisOpen}
                onClose={() => setIsAvisOpen(false)}
                programName={programName}
                formationSlug={formationSlug}
            />
        </section>
    );
};

export default Testimonials;
